// ============================================================
// Selection Types (must be before message types)
// ============================================================

export type MapId = 'candy_world' | 'giant_bathroom' | 'desert_canyon' | 'space_station' | 'haunted_mansion' | 'ice_world';
export type KartId = 'speed_demon' | 'road_hugger';

// ============================================================
// Network Messages
// ============================================================

// Client → Server
export type ClientMessage =
  | { type: 'create_room'; playerName: string }
  | { type: 'join_room'; roomCode: string; playerName: string }
  | { type: 'toggle_ready' }
  | { type: 'start_game' }
  | { type: 'input'; seq: number; input: PlayerInput }
  | { type: 'create_solo'; playerName: string; kartId: KartId; mapId: MapId }
  | { type: 'ping'; t: number };

// Server → Client
export type ServerMessage =
  | { type: 'room_created'; roomCode: string; playerId: string }
  | { type: 'room_joined'; roomCode: string; playerId: string; players: LobbyPlayer[] }
  | { type: 'lobby_update'; players: LobbyPlayer[] }
  | { type: 'countdown'; seconds: number }
  | { type: 'race_start'; yourIndex: number; mapId: MapId }
  | {
      type: 'snapshot';
      tick: number;
      time: number;
      karts: KartState[];
      items: ItemBoxState[];
      projectiles: ProjectileState[];
      bananas: BananaState[];
      coins: CoinState[];
      lastSeq: Record<string, number>;
    }
  | { type: 'item_pickup'; playerId: string; item: ItemType; boxIndex: number }
  | { type: 'player_hit'; playerId: string; byPlayerId: string }
  | { type: 'lap_complete'; playerId: string; lap: number }
  | { type: 'race_finish'; rankings: RaceResult[] }
  | { type: 'player_left'; playerId: string; newHostId?: string }
  | { type: 'lightning_strike'; byPlayerId: string }
  | { type: 'blooper_ink'; byPlayerId: string }
  | { type: 'error'; message: string }
  | { type: 'pong'; t: number };

// ============================================================
// Game State Types
// ============================================================

export interface PlayerInput {
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
  drift: boolean;
  item: boolean;
  trick: boolean;
}

export const EMPTY_INPUT: PlayerInput = {
  forward: false,
  backward: false,
  left: false,
  right: false,
  drift: false,
  item: false,
  trick: false,
};

export interface LobbyPlayer {
  id: string;
  name: string;
  ready: boolean;
  color: number;
  isHost: boolean;
}

export interface KartState {
  id: string;
  name: string;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  yaw: number;
  speed: number;
  steering: number;
  lap: number;
  checkpoint: number;
  progress: number;
  position: number;
  item: ItemType | null;
  stunTimer: number;
  boostTimer: number;
  finished: boolean;
  finishTime: number;
  color: number;
  // New fields for Mario Kart mechanics
  driftTime: number;          // A1: accumulated drift charge time
  coins: number;              // A6: current coin count (0-10)
  slipstreamTimer: number;    // A3: drafting charge accumulator
  starTimer: number;          // B7: invincibility timer
  shrinkTimer: number;        // B6: lightning shrunk timer
  blooperTimer: number;       // B9: ink on screen timer
  wrongWay: boolean;          // G7: facing wrong direction
  offRoad: boolean;           // A5: off the track surface
  heldItemBehind: ItemType | null;  // B11: item displayed behind kart as shield
  orbitItems: number;         // B5: count of orbiting items (0-3)
  rouletteTimer: number;      // B10: item roulette animation timer
  trickBoosting: boolean;     // A4: trick boost pending on landing
}

export enum ItemType {
  SpeedBoost = 'speed_boost',
  Projectile = 'projectile',
  GreenShell = 'green_shell',
  RedShell = 'red_shell',
  Banana = 'banana',
  TripleGreenShell = 'triple_green_shell',
  TripleRedShell = 'triple_red_shell',
  TripleBanana = 'triple_banana',
  Lightning = 'lightning',
  Star = 'star',
  BlueShell = 'blue_shell',
  Blooper = 'blooper',
}

export interface ItemBoxState {
  index: number;
  x: number;
  z: number;
  active: boolean;
  respawnTimer: number;
}

export interface ProjectileState {
  id: string;
  ownerId: string;
  x: number;
  y: number;
  z: number;
  vx: number;
  vz: number;
  shellType: 'green' | 'red' | 'blue' | 'legacy';
  bounceCount: number;
}

export interface BananaState {
  id: string;
  ownerId: string;
  x: number;
  z: number;
}

export interface CoinState {
  index: number;
  x: number;
  z: number;
  active: boolean;
  respawnTimer: number;
}

export interface RaceResult {
  playerId: string;
  playerName: string;
  time: number;
  position: number;
}

// ============================================================
// Kart Definition
// ============================================================

export interface KartDef {
  id: KartId;
  name: string;
  description: string;
  speedMult: number;
  accelMult: number;
  handlingMult: number;
  bodyColor: number;
  accentColor: number;
}

// ============================================================
// Map Theme
// ============================================================

export interface MapTheme {
  roadColor: number;
  roadStripeColor: number;
  wallColor: number;
  groundColor: number;
  skyColor: number;
  fogColor: number;
  fogNear: number;
  fogFar: number;
  ambientColor: number;
  ambientIntensity: number;
  sunColor: number;
  sunIntensity: number;
  sunPosition: { x: number; y: number; z: number };
}

// ============================================================
// Track Types
// ============================================================

export interface Vec2 {
  x: number;
  z: number;
}

export interface TrackPoint extends Vec2 {
  nx: number;
  nz: number; // normal (perpendicular to track direction)
  dx: number;
  dz: number; // tangent (track direction)
}

// ============================================================
// Hazard & Shortcut Types
// ============================================================

export type HazardType = 'boulder' | 'laser_gate' | 'ghost' | 'ice_crack';

export interface HazardDef {
  type: HazardType;
  pathFraction: number;      // position along track (0-1)
  lateralOffset: number;     // offset from center (-1 to 1, normalized to road width)
  behavior: 'static' | 'moving' | 'timed';
  speed?: number;            // for moving hazards
  onTime?: number;           // for timed hazards (seconds)
  offTime?: number;          // for timed hazards (seconds)
}

export interface ShortcutDef {
  entryFraction: number;     // track position of entry
  exitFraction: number;      // track position of exit
  controlPoints: Vec2[];     // shortcut path control points
  width: number;             // shortcut road width
}

export type SurfaceType = 'normal' | 'ice' | 'sand' | 'space';

// ============================================================
// Constants (sourced from shared/config.ts)
// ============================================================

import { config } from './config';

export const TICK_RATE = config.network.tickRate;
export const TICK_MS = 1000 / config.network.tickRate;
export const MAX_PLAYERS = config.network.maxPlayers;
export const MIN_PLAYERS = config.network.minPlayers;
export const TOTAL_LAPS = config.race.totalLaps;
export const COUNTDOWN_SECS = config.race.countdown;
export const SOLO_BOT_COUNT = config.race.soloBotCount;

// Physics
export const MAX_SPEED = config.physics.maxSpeed;
export const ACCELERATION = config.physics.acceleration;
export const BRAKE_FORCE = config.physics.brakeForce;
export const FRICTION = config.physics.friction;
export const STEER_SPEED = config.physics.steerSpeed;
export const MAX_STEER = config.physics.maxSteer;
export const STEER_RETURN = config.physics.steerReturn;
export const DRIFT_FACTOR = config.physics.driftFactor;
export const KART_RADIUS = config.physics.kartRadius;
export const BOUNCE = config.physics.bounce;

// Items
export const BOOST_MULT = config.items.boostMultiplier;
export const BOOST_DURATION = config.items.boostDuration;
export const PROJECTILE_SPEED = config.items.projectileSpeed;
export const PROJECTILE_RADIUS = config.items.projectileRadius;
export const STUN_DURATION = config.items.stunDuration;
export const ITEM_RESPAWN = config.items.itemRespawn;
export const ITEM_BOX_RADIUS = config.items.itemBoxRadius;

// Ramps & Gravity
export const RAMP_HEIGHT = config.physics.rampHeight;
export const RAMP_LENGTH = config.physics.rampLength;
export const RAMP_WIDTH_MULT = config.physics.rampWidthMult;
export const GRAVITY = config.physics.gravity;
export const RAMP_LANDING_BOOST = config.physics.rampLandingBoost;

// Track
export const ROAD_WIDTH = config.track.roadWidth;
export const WALL_HEIGHT = config.track.wallHeight;
export const POINTS_PER_SEGMENT = config.track.pointsPerSegment;

// Colors for karts (12 for up to 12 racers in solo mode)
export const KART_COLORS = [
  0xff4444, // red
  0x4488ff, // blue
  0x44ff44, // green
  0xffff44, // yellow
  0xff44ff, // magenta
  0x44ffff, // cyan
  0xff8844, // orange
  0x8844ff, // purple
  0xff6688, // pink
  0x88ff66, // lime
  0x6688ff, // sky blue
  0xffaa44, // gold
];
